import { MapBounds } from '@/pages/AuctionsNearMe/components/MapView/components/GoogleMap/AuctionsNearMeGoogleMap';
import {
    defaultAuctionsNearMeUkBounds,
    defaultAuctionsNearMeUsaBounds,
} from '@/pages/AuctionsNearMe/constants/AuctionsNearMeConstants';
import { createBoundingBoxString } from '@/pages/AuctionsNearMe/helpers/createBoundingBoxString';
import {
    findGeoAreaFromUrlParams,
    getLegacyGeoAreaIdFromUrlParams,
    getStateCodeFromName,
} from '@/pages/AuctionsNearMe/helpers/geoAreaToUrlParams';
import { getPageSizeFromQueryParams } from '@/pages/AuctionsNearMe/helpers/getPageSizeFromQueryParams';
import { isPlausibleLocation } from '@/pages/AuctionsNearMe/helpers/isPlausibleLocation';
import {
    AuctionsNearMeQueryParams,
    AuctionsNearMeRouteParams,
} from '@/pages/AuctionsNearMe/types/AuctionsNearMe.types';
import {
    fetchCatalogsNearMeByBounds,
    fetchCatalogsNearMeByCityAndState,
} from '@/redux/modules/auctionsNearMe/auctionsNearMe.actions';
import { markLocationInvalid } from '@/redux/modules/auctionsNearMe/auctionsNearMe.reducer';
import { getCatalogsNearMeSelector } from '@/redux/modules/auctionsNearMe/auctionsNearMe.selectors';
import { fetchCatalogAdIfNeeded } from '@/redux/modules/catalog/ad/catalogAd.actions';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalogs/catalog.actions';
import { loadAllGeoAreas } from '@/redux/modules/geoArea/geoArea';
import { DataLoaderParams } from '@/types/DataLoaderParams';
import { getPageFromQueryParams, parseQueryParams } from '@/utils/queryParams';

const auctionsNearMePageData = async ({ location, match, store }: DataLoaderParams<AuctionsNearMeRouteParams>) => {
    const { dispatch, getState } = store;

    // Parse query parameters to check for custom search
    const queryParams = parseQueryParams<AuctionsNearMeQueryParams>(location.search);

    // If there's a search query, skip fetching - the component will handle it via geocoding
    if (queryParams.search) {
        // Still load geoAreas and fetch ad, but don't fetch catalogs
        await Promise.all([
            dispatch(loadAllGeoAreas()),
            dispatch(fetchCatalogAdIfNeeded({ pageName: 'leaderboard-auctions-near-me' })),
        ]);
        return;
    }

    const { city, country, state } = match.params;
    // Reads ?page=N so directly opening a paginated URL renders that page's data instead of
    // always page 1 — the canonical tag already reflects ?page=N regardless, so leaving this
    // hardcoded served a canonical that misrepresented the content underneath it.
    const page = getPageFromQueryParams(queryParams);
    // Same reason as `page`: the pagination picker writes ?pageSize= to the URL, so a direct load
    // has to render that many results or SSR and the client disagree on what the page contains.
    const pageSize = getPageSizeFromQueryParams(queryParams);
    const adFetchPromise = dispatch(fetchCatalogAdIfNeeded({ pageName: 'leaderboard-auctions-near-me' }));

    // Two loads of the same page can overlap: the second one starts before the first has answered.
    // If that first request then fails, marking the location invalid would 404 a page the second
    // one just filled with valid data — so only the newest request in flight is allowed to mark.
    // The reducer runs this same check on the parts of the rejection it still handles itself.
    const isSupersededRequest = (requestId: string) => getState().auctionsNearMe.latestRequestId !== requestId;

    // Hydrates the SSR markup with the full catalog records behind the results.
    const fetchCatalogDetails = async () => {
        const catalogIds = getCatalogsNearMeSelector(getState()).map((catalog) => catalog.id);

        if (catalogIds.length > 0) {
            await dispatch(fetchCatalogsIfNeeded({ catalogIds }));
        }
    };

    // Legacy /{geoAreaId}/{geoAreaName}/ links resolve by id further down, not as a city/state pair —
    // the first segment is an id, so the state validation below would reject every one of them.
    const legacyGeoAreaId = getLegacyGeoAreaIdFromUrlParams({ city, state });

    // City + state URLs (e.g. /find-auctions-near-me/fl/st-petersburg):
    // Use the city/state endpoint so the backend resolves the location.
    // Avoids relying on the client-side Google Maps geocoder which leaves SSR
    // showing the default USA bounds count (~1000+) instead of the real count.
    if (city && state && !legacyGeoAreaId) {
        // near-me-v2 filters on the 2-letter code, so a full state name ("ohio") returns zero
        // results rather than an error. Reject what does not resolve at all (or just repeats the
        // city segment) before spending a backend call on it.
        if (!isPlausibleLocation(city, state)) {
            dispatch(markLocationInvalid(location.pathname));
            await adFetchPromise;
            return;
        }

        const [, cityAndStateAction] = await Promise.all([
            dispatch(loadAllGeoAreas()),
            dispatch(
                fetchCatalogsNearMeByCityAndState({
                    city,
                    page,
                    pageSize,
                    state: getStateCodeFromName(state),
                })
            ),
            adFetchPromise,
        ]);

        // catalog-management answers an unknown city slug with a flat 500, indistinguishable from
        // a genuine outage. Treating both as invalid is the agreed trade-off: a 404 beats
        // rendering "no auctions" for a city that does not exist.
        if (fetchCatalogsNearMeByCityAndState.rejected.match(cityAndStateAction)) {
            if (!isSupersededRequest(cityAndStateAction.meta.requestId)) {
                dispatch(markLocationInvalid(location.pathname));
            }
            return;
        }

        await fetchCatalogDetails();
        return;
    }

    await dispatch(loadAllGeoAreas());

    // Find geoArea from URL params (state-only, country-only, or a legacy id)
    const allGeoAreas = Object.values(getState().geoArea.byId);
    const geoArea = legacyGeoAreaId
        ? allGeoAreas.find(({ geoAreaId }) => geoAreaId === legacyGeoAreaId)
        : findGeoAreaFromUrlParams(allGeoAreas, {
              country,
              state,
          });

    // Single-segment URLs (/find-auctions-near-me/colorado/, /wyoming/, /tronto/) carry a location
    // we have to resolve ourselves. Only the bare /find-auctions-near-me/ landing page is allowed
    // to fall back to the default country bounds — doing so for an unresolved segment is what
    // served the whole ~1,200-auction catalog under URLs like /normal-business/.
    // Reached with a `city` only on a legacy /{geoAreaId}/{geoAreaName}/ URL whose id did not
    // resolve above — a bogus id, so letting it fall through to the invalid check is correct.
    // Every other city URL was already handled and returned from in the branch above.
    const hasLocationSegment = Boolean(country || state);

    if (hasLocationSegment && !geoArea) {
        // Only 24 US states have a geoArea record, so a miss does not mean the place is fake.
        // A real state or province still has auctions worth showing; query it by code instead.
        // `:state` and `:country` are same-rank routes matched in declaration order, so the
        // segment lands in `state` today — read both so a route reorder cannot silently 404 it.
        const stateCode = getStateCodeFromName(state || country || '');

        if (stateCode) {
            const [stateOnlyAction] = await Promise.all([
                dispatch(
                    fetchCatalogsNearMeByCityAndState({
                        page,
                        pageSize,
                        state: stateCode,
                    })
                ),
                adFetchPromise,
            ]);

            // Same trade-off as the city/state branch above, and the same blind spot: the state
            // segment already resolved to a real code here, so a rejection is more likely an
            // outage than a bad URL, and 404ing it is the behavior this page has always had.
            if (fetchCatalogsNearMeByCityAndState.rejected.match(stateOnlyAction)) {
                if (!isSupersededRequest(stateOnlyAction.meta.requestId)) {
                    dispatch(markLocationInvalid(location.pathname));
                }
                return;
            }

            await fetchCatalogDetails();
            return;
        }

        // An empty geoArea list means the geographic API failed, not that the URL is bad —
        // keep the old default-bounds behavior rather than 404ing every location page.
        if (allGeoAreas.length > 0) {
            dispatch(markLocationInvalid(location.pathname));
            await adFetchPromise;
            return;
        }
    }

    let mapBounds: MapBounds =
        getState().intl.locale.toLowerCase() === 'en-gb'
            ? defaultAuctionsNearMeUkBounds
            : defaultAuctionsNearMeUsaBounds;

    if (geoArea) {
        mapBounds = {
            northEast: {
                lat: geoArea.northEastLatitude,
                lng: geoArea.northEastLongitude,
            },
            southWest: {
                lat: geoArea.southWestLatitude,
                lng: geoArea.southWestLongitude,
            },
        };
    }

    // Fetch catalogs within the map bounds
    const insideBoundingBox = createBoundingBoxString(mapBounds);

    await Promise.all([
        dispatch(
            fetchCatalogsNearMeByBounds({
                insideBoundingBox,
                page,
                pageSize,
            })
        ),
        adFetchPromise,
    ]);

    await fetchCatalogDetails();
};

export default auctionsNearMePageData;
